/**
 * projectCache — typed wrapper over the Rust cache_read / cache_write /
 * cache_clear commands.
 *
 * The cache lives at `<projectRoot>/.ateliercode/cache/<key>.json`. Writes
 * are atomic (tmp + rename in Rust) and `.ateliercode/.gitignore` is
 * auto-managed by the Rust side so cache files never enter commits.
 *
 * Each consumer (file tree, search index, TS workspace, symbol index) owns
 * one key. The wire format is whatever JSON shape the consumer needs — this
 * module just shuttles strings.
 */
import { logger } from '@/lib/logger';
import { getBackend } from '@/services/backend';

const TAG = 'ProjectCache';

/** Stable cache keys. Keep them flat — no slashes (Rust validates). */
export const CacheKeys = {
  fileTree: 'file-tree',
  fileIndex: 'file-index',
  tsWorkspace: 'ts-workspace',
  symbols: 'symbols',
  /** Multi-file editor state: open tab list, active tab, scroll/cursor per file. */
  editorState: 'editor-state',
} as const;

export type CacheKey = (typeof CacheKeys)[keyof typeof CacheKeys];

export async function readCache<T>(projectId: string, key: CacheKey): Promise<T | null> {
  try {
    const raw = await getBackend().files.cacheRead(projectId, key);
    if (raw == null) return null;
    return JSON.parse(raw) as T;
  } catch (e) {
    logger.debug(TAG, `cache_read(${key}) failed:`, e);
    return null;
  }
}

export async function writeCache<T>(projectId: string, key: CacheKey, value: T): Promise<void> {
  try {
    await getBackend().files.cacheWrite(projectId, key, JSON.stringify(value));
  } catch (e) {
    // Caching is best-effort — logging at debug rather than warn so a flaky
    // disk doesn't spam the console for an otherwise-functional session.
    logger.debug(TAG, `cache_write(${key}) failed:`, e);
  }
}

export async function clearProjectCache(projectId: string): Promise<void> {
  try {
    await getBackend().files.cacheClear(projectId);
  } catch (e) {
    logger.warn(TAG, 'cache_clear failed:', e);
    throw e;
  }
}

export async function clearAllProjectCaches(): Promise<number> {
  try {
    return await getBackend().files.cacheClearAll();
  } catch (e) {
    logger.warn(TAG, 'cache_clear_all failed:', e);
    throw e;
  }
}

// ─── Cache shapes (envelopes around payload + version + timestamp) ──────────

/**
 * Every cache entry is wrapped in an envelope so we can rev the format
 * without confusing future code. `cachedAt` is informational; cache keys are
 * single-use so we only store the latest snapshot.
 */
export interface CacheEnvelope<T> {
  /** Bump when the payload shape changes incompatibly. */
  schemaVersion: number;
  cachedAt: number;
  payload: T;
}

export function envelope<T>(payload: T, schemaVersion: number): CacheEnvelope<T> {
  return { schemaVersion, cachedAt: Date.now(), payload };
}
